import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useSignal } from "@preact/signals";
import Modal from "../Modal";
import TextInput from "../Form/TextInput";
import ModalFooter from "../ModalFooter";
import Button from "../Button";
import TextArea from "../Form/TextArea";
import Label from "../Form/Label";
import { Api } from "../../lib/api";
import { appendTimezone, trimSeconds } from "../../lib/date";
import useMessageChannel from "../../lib/hooks/use-message-channel";
import Loader from "../Loader";
import MessageBox from "../MessageBox";
import DateTimeInput from "../Form/DateTimeInput";
import Select from "../Form/Select";
import SelectInput from "../Form/Select/SelectInput";
import { SelectListOption } from "../Form/Select/SelectListOption";
import { msToSeconds } from "../../lib/number";
import style from "./style.module.scss";
export const EVENT_UPDATE_NOTE = "update-note";
export default function ModalNote(props) {
    const { isOpen = false } = props;
    const requestCloseModal = () => {
        if (props.onClose) {
            props.onClose();
        }
    };
    return (_jsx(Modal, { isOpen: isOpen, onClose: requestCloseModal, id: "add-deploy-modal", title: props.id ? "Edit Note" : "Add Note", width: "medium", children: _jsx("div", { className: style["modal-add-note"], children: _jsx(DataForm, Object.assign({}, props, { onClose: requestCloseModal })) }) }));
}
function DataForm(props) {
    const { onClose } = props;
    const defaultTimestamp = trimSeconds(msToSeconds(Date.now()));
    const timestamp = props.timestamp || defaultTimestamp;
    const ALL_SITES_VALUE = "0";
    const name = props.name ? props.name : "";
    const description = props.description ? props.description : "";
    const errors = useSignal(null);
    const isLoading = useSignal(false);
    const sitesDefault = [];
    const sites = useSignal(props.sites);
    if (Array.isArray(sites.value) && sites.value.length > 0) {
        sites.value.forEach((site) => {
            sitesDefault.push({ value: site.toString(), label: "" });
        });
    }
    else {
        sites.value = [ALL_SITES_VALUE];
        sitesDefault.push({ value: ALL_SITES_VALUE, label: "All Sites", toggle: true });
    }
    const sitesSelected = useSignal(sitesDefault);
    const siteOptions = window.SPEEDCURVE_CONFIG.siteIds;
    //Add the all sites option to the menu.
    const hasAllSites = siteOptions.findIndex(({ value }) => value === ALL_SITES_VALUE);
    if (hasAllSites < 0) {
        siteOptions.splice(0, 0, { label: "All Sites", value: ALL_SITES_VALUE, toggle: true });
    }
    else {
        //Update the All Sites option to make sure it's a togglable option.
        siteOptions[hasAllSites].toggle = true;
    }
    const channel = useMessageChannel();
    function saveNote(e) {
        e.preventDefault();
        isLoading.value = true;
        errors.value = null;
        // make sure site values are strings. the backend sometimes gives us numbers
        if (Array.isArray(sites.value)) {
            sites.value = sites.value.map((site) => site.toString());
        }
        const form = e.currentTarget.elements;
        const timestamp = form.datetime.valueAsOffsetTimestamp;
        const name = form.name.value;
        const description = form.description.value;
        const data = {
            sites: sites.value,
            timestamp,
            name,
            description,
        };
        if (props.id) {
            Api.patch("/notes/" + props.id, data, () => {
                channel.send(EVENT_UPDATE_NOTE, {
                    id: props.id,
                    name,
                    description,
                    time: timestamp,
                    type: "note",
                    sites: sites.value || [],
                });
                isLoading.value = false;
                onClose();
            }, (error) => {
                isLoading.value = false;
                errors.value = error;
            });
        }
        else {
            Api.post("/notes/", data, ({ id }) => {
                channel.send(EVENT_UPDATE_NOTE, {
                    id,
                    name,
                    description,
                    time: timestamp,
                    type: "note",
                    sites: sites.value || [],
                });
                isLoading.value = false;
                onClose();
            }, (error) => {
                isLoading.value = false;
                errors.value = error;
            });
        }
    }
    function deleteNote(e) {
        e.preventDefault();
        isLoading.value = true;
        errors.value = null;
        // make sure site values are strings
        if (Array.isArray(sites.value)) {
            sites.value = sites.value.map((site) => site.toString());
        }
        Api.delete(`/notes/${props.id}`, () => {
            channel.send(EVENT_UPDATE_NOTE, {
                id: props.id,
                name,
                description,
                time: timestamp,
                type: "note",
                sites: sites.value || [],
                action: "delete",
            });
            isLoading.value = false;
            onClose();
        }, (error) => {
            isLoading.value = false;
            errors.value = error;
        });
    }
    function updateSite(newSites) {
        if (Array.isArray(newSites)) {
            sitesSelected.value = newSites;
            sites.value = newSites.map((site) => site.value.toString());
        }
    }
    let showDelete = null;
    if (props.id) {
        showDelete = (_jsx(Button, { variation: "tertiary-inline", onClick: deleteNote, children: "Delete Note" }, "delete"));
    }
    return (_jsxs("form", { method: "post", onSubmit: saveNote, children: [isLoading.value ? (_jsx("div", { className: style["modal-loading"], children: _jsx(Loader, {}) })) : null, _jsxs("div", { className: style["modal-add-note__form-item"], children: [_jsx(Label, { children: "Sites" }), _jsx(Select, { options: siteOptions, renderSelected: (selected, editSelected) => {
                            return _jsx(SelectInput, { options: selected, onEdit: editSelected });
                        }, renderOption: (selectOption, selectedState) => {
                            return _jsx(SelectListOption, { option: selectOption, selected: selectedState, multiple: true });
                        }, defaultValue: sitesSelected.value, onChange: updateSite, multiple: true })] }), _jsxs("div", { className: style["modal-add-note__form-item"], children: [_jsx(Label, { children: appendTimezone("Date & Time") }), _jsx(DateTimeInput, { name: "datetime", step: "60", defaultValueAsTimestamp: timestamp, className: style["date-range-picker__input"] })] }), _jsxs("div", { className: style["modal-add-note__form-item"], children: [_jsx(Label, { children: "Name" }), _jsx(TextInput, { name: "name", defaultValue: name, required: true })] }), _jsxs("div", { className: style["modal-add-note__form-item"], children: [_jsx(Label, { children: "Description" }), _jsx("div", { children: _jsx(TextArea, { name: "description", rows: 6, defaultValue: description }) })] }), errors.value ? displayServerError(errors.value) : null, _jsxs(ModalFooter, { rightContent: showDelete, children: [_jsx(Button, { variation: "primary", type: "submit", children: "Save" }, "addNote"), _jsx(Button, { variation: "tertiary-inline", onClick: () => onClose(), children: "Cancel" }, "close")] })] }));
}
function displayServerError(errors) {
    return errors.message && _jsx(MessageBox, { type: "error", children: errors.message });
}
